// Configuration par thème (style visuel). Tous les thèmes partagent le même
// gabarit de nez (nez Grok pré-positionné en 240×160, masques réutilisés) ;
// seuls `key`, `label` et `fingers` changent.

import type { SpriteName } from "./assets";

export type ThemeKey = "pixel" | "simpsons" | "doodle" | "zombie";

export interface Finger {
  name: SpriteName;
  tips: number[]; // x du/des bout(s) de doigt dans le sprite (px natifs)
  tipY: number; // y du bout dans le sprite (px natifs)
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Theme {
  key: ThemeKey;
  label: string;
  /** Zone du sprite de nez à prélever. */
  noseSrc: Rect;
  /** Où la dessiner sur le canvas 240×160. */
  noseDst: Rect;
  /** Narines visées (coordonnées écran). */
  nostrilL: number;
  nostrilR: number;
  nostrilY: number;
  /** Bords horizontaux du nez (écran). Hors de [noseLeft, noseRight] le doigt passe
   *  à côté du nez → raté sans réaction des narines. */
  noseLeft: number;
  noseRight: number;
  /** Hauteur du bout du doigt au repos. */
  restTipY: number;
  /** Profondeur d'enfoncement de la pointe dans la narine. */
  pokeDepth: number;
  /** Doigts du thème (1 doigt → vise une narine ; 2 doigts → les deux). */
  fingers: Finger[];
  /** Frames de réaction du nez sur un raté (animées). Le repos/réussite = "nez_0".
   *  left = raté côté gauche (1 doigt), right = côté droit, both = 2 doigts. */
  reactions: { left: SpriteName[]; right: SpriteName[]; both: SpriteName[] };
}

// Gabarit partagé : tout sauf l'identité (key/label) et les doigts.
const BASE: Omit<Theme, "key" | "label" | "fingers"> = {
  noseSrc: { x: 0, y: 0, w: 480, h: 320 },
  noseDst: { x: 0, y: 0, w: 240, h: 160 },
  nostrilL: 101,
  nostrilR: 138,
  nostrilY: 65,
  noseLeft: 82,
  noseRight: 157,
  restTipY: 110,
  pokeDepth: 40,
  reactions: { left: ["nez_1"], right: ["nez_2"], both: ["nez_3"] },
};

// Les 4 doigts d'un thème, à partir des positions de pointe (index, majeur,
// annulaire, puis le doigt double index+majeur). Tous pointe en haut (tipY 0).
function fingers(index: number, majeur: number, annulaire: number, both: [number, number]): Finger[] {
  return [
    { name: "index", tips: [index], tipY: 0 },
    { name: "majeur", tips: [majeur], tipY: 0 },
    { name: "annulaire", tips: [annulaire], tipY: 0 },
    { name: "index-majeur", tips: both, tipY: 0 },
  ];
}

function theme(key: ThemeKey, label: string, fingers: Finger[]): Theme {
  return { ...BASE, key, label, fingers };
}

export const THEMES: Record<ThemeKey, Theme> = {
  pixel: theme("pixel", "Pixel", fingers(218, 156, 69, [69, 156])),
  simpsons: theme("simpsons", "Simpsons", fingers(219, 156, 70, [70, 157])),
  doodle: theme("doodle", "Doodle", fingers(220, 161, 72, [71, 158])),
  zombie: theme("zombie", "Zombie", fingers(227, 179, 75, [86, 179])),
};
